/**
 * WelcomeValueCards — the three value-prop cards for onboarding step 1.
 *
 * Resells the Voice promise before building the first genie: always on, in your
 * voice & knowledge, live everywhere fast. Each card is an icon chip + title +
 * subline, mirroring the wireframe's `welcome-value-cards` block.
 *
 * Brand law: genie tokens only; wireframe purple (rocket) maps to the signature
 * gradient chip so the three cards stay distinct without a banned colour. Brand
 * GenieIcon for the chips. No "AI" language; no em dashes.
 */
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type ChipStyle = "secondary" | "primary" | "gradient";

interface ValueCardData {
  id: string;
  genieIcon: GenieIconName;
  chip: ChipStyle;
  label: string;
  desc: string;
}

const VALUE_CARDS: ValueCardData[] = [
  {
    id: "always-on",
    genieIcon: "phone",
    chip: "secondary",
    label: "Answers, around the clock",
    desc: "Never miss a call or a message. Always on, multilingual, instant.",
  },
  {
    id: "your-voice",
    genieIcon: "microphone",
    chip: "primary",
    label: "In your voice, your knowledge",
    desc: "A voice you choose, hard wired to your brand and your content.",
  },
  {
    id: "out-there",
    genieIcon: "share",
    chip: "gradient",
    label: "Out there in minutes",
    desc: "Phone, website, QR code or a link, wherever your customers are.",
  },
];

const chipClasses: Record<ChipStyle, string> = {
  secondary: "bg-genie-secondary/10",
  primary: "bg-genie-primary/10",
  gradient: "bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]",
};

/** Icon paint per chip: tinted to match the chip, white on the gradient. */
const chipIconColor: Record<ChipStyle, { color: string; secondaryColor?: string }> = {
  secondary: { color: "#4E9CFF" },
  primary: { color: "#FF4DB8" },
  gradient: { color: "#FFFFFF" },
};

export const WelcomeValueCards = () => {
  return (
    <div
      data-hg-block="welcome-value-cards"
      className="w-full flex flex-col gap-3"
    >
      {VALUE_CARDS.map(({ id, genieIcon, chip, label, desc }) => (
        <div
          key={id}
          data-hg-block={id}
          className="flex items-center gap-4 rounded-2xl border border-genie-border bg-white p-4 md:p-5 shadow-sm"
        >
          <div
            className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${chipClasses[chip]}`}
          >
            <GenieIcon
              name={genieIcon}
              size={24}
              color={chipIconColor[chip].color}
              secondaryColor={chipIconColor[chip].secondaryColor}
              label={label}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-[15.5px] font-bold tracking-[-0.2px] text-[#222B45]">
              {label}
            </p>
            <p className="text-[13px] leading-relaxed text-[#64748B]">{desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WelcomeValueCards;
